import React, { FC, useRef, useEffect, useMemo, useState } from 'react';
import PureVideo, { IPureVideoRef } from '@base/lib/PureVideo';
import { useInViewport } from 'ahooks';
import { IBannerVideo } from 'mod/featureV2/interface/banner';
import { BannerPlaceholder } from 'mod/featureV2/Banner/BannerPlaceholder';
import { IBannerProps } from 'mod/featureV2/Banner/interface';
import { getWindowWidth } from 'ava-util/dist/dom-window';
import { getBannerStyle } from 'mod/featureV2/Banner/utils';

type IProps = IBannerProps<IBannerVideo>;

export const Main: FC<IProps> = props => {
    const {
        banner,
        threshold = [0.1, 0.4, 1],
        playThreshold = 0.4,
        largeScreenHeightFixed,
    } = props;

    const { bgColor, href, target, src, backup, largePoster, ...rest } = banner;

    let {
        autoPlay = false,
        control = { opacity: 0 },
        controls = false,
        ...restVideoProps
    } = rest;


    // 带链接时模拟 gif，自动循环播放
    if (!!href) {
        autoPlay = true;
        control = false;
        controls = false;
    }

    const videoRef = useRef<IPureVideoRef>(null);
    const wrapRef = useRef<HTMLDivElement>(null);

    const [inViewport, ratio] = useInViewport(() => wrapRef?.current, {
        threshold, // 可以是单一的 number 也可以是 number 数组，target 元素和 root 元素相交程度达到该值的时候 ratio 会被更新。
        // root: () => document.getElementById('parent'), //未指定 浏览器窗口
    })

    const inViewHandle = () => {
        if (!videoRef.current) {
            return
        }
        // 如果自动播放
        const { play, pause, status } = videoRef.current
        if (videoRef.current && autoPlay) {
            if (!inViewport) {
                pause()
            } else {
                if (status === 'paused' && ratio && ratio >= playThreshold) {
                    play()

                } else if (status === 'playing' && ratio && ratio < playThreshold) {
                    pause()
                }
            }
        }
    }

    useEffect(() => {
        inViewHandle()
    }, [inViewport, ratio])

    // 播放器 渲染控制 （ratio的变化重复渲染， 导致 m3u8 流媒体视频 每次都又从开头播放）
    return useMemo(() => {
        return (
            <PureVideo
                {...restVideoProps}
                className={'sw-overlay'}
                ref={videoRef}
                src={!backup ? src : [src, backup]}
                autoPlay={autoPlay}
                control={control}
                controls={controls}
                canMultiplePlay
                fit={'cover'}
                wrapProps={{
                    'data-t-tar': props['data-t-tar'],
                    'data-t-pos': props['data-t-pos']
                }}
            />
        )
    }, [banner])
};

export const BannerVideo: FC<IProps> = props => {
    const [calcProps, setCalcProps] = useState<IProps>();

    const {
        banner,
        width,
        height,
        onClick,
        largeScreenHeightFixed,
    } = props;

    useEffect(() => {
        if (getWindowWidth() > 1920 && banner.large) {
            setCalcProps({
                ...props,
                banner: {
                    ...banner,
                    src: banner.large,
                    poster: banner.largePoster || banner.poster
                }
            });
        } else {
            setCalcProps(props);
        }
    }, [banner]);

    return (
        <BannerPlaceholder
            className={'g-banner'}
            style={getBannerStyle(props)}
            href={banner.href}
            target={banner.target}
            width={width}
            height={height}
            bgColor={banner.bgColor}
            onClick={onClick}
            largeScreenHeightFixed={largeScreenHeightFixed}
        >
            {calcProps ? <Main {...calcProps}/> : null}
        </BannerPlaceholder>
    )
};